import {
  CheckIcon,
  Field,
  IconButton,
  LabelChip,
  NumberInput,
  RestoreIcon,
  SingleSelectDropdown,
  Slider,
  Switch,
  Text,
  TextInput,
} from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import {
  formatParamValue,
  taskDisplayOptions,
  paramValuesEqual,
  type EvalParamSpec,
  type EvalParamValue,
  type EvalParamValues,
} from "./evalParamSpecs";
import { resolveParamSlider } from "./evalParamSliders";
import { isSeedParamKey, safeSeedCount } from "./evalSeeds";
import styles from "./ParameterConfigForm.module.css";

type ParameterConfigFormProps = {
  specs: readonly EvalParamSpec[];
  values: EvalParamValues;
  onChange: (key: string, value: EvalParamValue) => void;
  onReset?: () => void;
  disabled?: boolean;
};

// 일반 파라미터는 두 열의 한 행이다. 두 열은 읽는 사람의 질문에 따라 나뉜다.
//   - 식별 열: "이 파라미터는 무엇이고 이미지의 기본값은 무엇인가" 에 답한다. 라벨, env key,
//     설명, 기본값을 표시한다.
//   - 컨트롤 열: "지금 값은 무엇인가" 에 답한다. 입력과 그 입력에 대한 액션을 표시한다.
// c6 Field 가 `layout="spread"` 로 배치를 소유한다. spread 는 컨트롤을 행의 오른쪽 끝에 붙인다.
// 그래서 남는 공간은 오른쪽에 쌓이지 않고 두 열 사이에 생긴다.
// TASK 는 설명 클릭으로 목록이 열리지 않도록 식별 정보를 label 밖에 두고 오른쪽 절반에 컨트롤을 둔다.
const SEED_DESCRIPTION = "쉼표로 구분한 SEED 마다 별도의 Evaluation이 실행됩니다.";

export function ParameterConfigForm({
  specs,
  values,
  onChange,
  onReset,
  disabled = false,
}: ParameterConfigFormProps) {
  return (
    <div className={styles.form}>
      <fieldset className={styles.fieldset} disabled={disabled}>
        <legend className={styles.legendWrap}>
          <span className={styles.legend}>
            <Text variant={hatcheryTextModes["caption-bold"]} className={styles.legendText}>
              파라미터 설정
            </Text>
            {onReset ? (
              <IconButton
                type="button"
                variant="surface"
                size="sm"
                text="기본값 복원"
                disabled={disabled}
                onClick={onReset}
              />
            ) : null}
          </span>
        </legend>
        <div className={styles.rows}>
          {specs.map((spec) => (
            <ParamRow
              key={spec.key}
              spec={spec}
              value={values[spec.key] ?? spec.default}
              onChange={onChange}
              disabled={disabled}
            />
          ))}
        </div>
      </fieldset>
    </div>
  );
}

function ParamRow({
  spec,
  value,
  onChange,
  disabled,
}: {
  spec: EvalParamSpec;
  value: EvalParamValue;
  onChange: (key: string, value: EvalParamValue) => void;
  disabled: boolean;
}): ReactNode {
  const seedParam = isSeedParamKey(spec.key);
  const description = seedParam ? (spec.description ?? SEED_DESCRIPTION) : spec.description;
  const identity = <ParamIdentity spec={spec} description={description} />;

  return (
    <div className={`${styles.row} ${spec.selectsTask ? styles.taskRow : ""}`}>
      {spec.selectsTask ? (
        <Text variant={hatcheryTextModes["caption-bold"]}>{identity}</Text>
      ) : null}
      <Field
        label={spec.selectsTask ? undefined : identity}
        textVariant={hatcheryTextModes["caption-bold"]}
        layout={spec.selectsTask ? "stack" : "spread"}
        disabled={disabled}
      >
        <div className={styles.controlCell}>
          {/* 되돌릴 값이 없어도 슬롯은 폭을 유지한다. 그래서 행을 편집해도 컨트롤의 x 위치가
              열 전체에서 같다. */}
          <div className={styles.revertSlot}>
            <RevertButton spec={spec} value={value} onChange={onChange} disabled={disabled} />
          </div>
          <div className={styles.controlStack}>
            <ParamControl spec={spec} value={value} onChange={onChange} disabled={disabled} />
            {seedParam ? seedCountNote(value) : null}
          </div>
        </div>
      </Field>
    </div>
  );
}

// 일반 파라미터의 식별 열은 Field 의 <label> 이다. TASK 는 일반 텍스트로 둔다.
// 각 컨트롤은 자기 aria-label 을 유지하고, 그 값이 접근성 이름이 된다.
function ParamIdentity({
  spec,
  description,
}: {
  spec: EvalParamSpec;
  description?: string;
}): ReactNode {
  return (
    <span className={styles.identity}>
      <span className={styles.name}>{spec.label}</span>
      {/* label 이 없는 metadata 는 env key 를 label 로 쓴다. 그래서 key 가 label 과 다를 때만 key 를 표시한다. */}
      {spec.key === spec.label ? null : <span className={styles.key}>{spec.key}</span>}
      {description === undefined ? null : <span className={styles.description}>{description}</span>}
      <span className={styles.default}>기본값: {formatParamValue(spec.default)}</span>
    </span>
  );
}

// metadata spec 마다 컨트롤을 렌더링한다: 쉼표로 구분한 seed 입력, 문자열 입력, enum 드롭다운,
// boolean 스위치, 또는 선택적 범위 슬라이더가 있는 숫자 필드.
function ParamControl({
  spec,
  value,
  onChange,
  disabled,
}: {
  spec: EvalParamSpec;
  value: EvalParamValue;
  onChange: (key: string, value: EvalParamValue) => void;
  disabled: boolean;
}): ReactNode {
  if (spec.type === "boolean") {
    return (
      <Switch
        checked={value === true}
        onChange={(event) => onChange(spec.key, event.target.checked)}
        aria-label={spec.label}
      />
    );
  }

  // SIMULATION_SEED 는 쉼표로 구분한 값을 받는다. 값마다 독립 시나리오를 하나 만든다.
  if (isSeedParamKey(spec.key)) {
    return (
      <TextInput
        value={String(value ?? "")}
        aria-label={spec.label}
        onChange={(event) => onChange(spec.key, event.target.value)}
        placeholder="SEED 값을 쉼표로 구분해 입력해 주세요 (예: 1, 33, 2523)."
      />
    );
  }

  if (spec.type === "string") {
    return (
      <TextInput
        value={String(value)}
        aria-label={spec.label}
        onChange={(event) => onChange(spec.key, event.target.value)}
      />
    );
  }

  if (spec.type === "enum") {
    // 이미지가 선택지마다 설명을 제공한다. 그래서 드롭다운이 각 선택지 아래에 설명을 표시한다.
    const sourceOptions = spec.selectsTask
      ? taskDisplayOptions(spec.options ?? [])
      : (spec.options ?? []);
    const options = sourceOptions.map((option) => ({
      value: option.value,
      label: option.value,
      description: option.description,
    }));
    const dropdown = (
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.default}
        className={spec.selectsTask ? styles.taskDropdown : undefined}
        // 선택된 배지로 트리거가 좁아져도 목록은 TASK 이름을 읽을 폭을 확보한다.
        fullWidth={spec.selectsTask === true}
        menuWidth={spec.selectsTask ? "min(28rem, calc(100vw - 2rem))" : undefined}
        label={spec.label}
        value={String(value)}
        options={options}
        renderOption={
          spec.selectsTask
            ? (option, context) => (
                <button
                  type="button"
                  className={styles.taskOption}
                  disabled={context.disabled}
                  data-selected={context.selected}
                >
                  <span className={styles.taskOptionTitle}>
                    <span
                      className={styles.taskCheck}
                      data-selected={context.selected}
                      aria-hidden="true"
                    >
                      <CheckIcon />
                    </span>
                    <span className={styles.taskOptionName}>{option.label}</span>
                    {sourceOptions.find((item) => item.value === option.value)?.kind ===
                    "sandbox" ? (
                      <LabelChip tone="neutral" size="xs" variant="filled" showIcon={false}>
                        Sandbox
                      </LabelChip>
                    ) : null}
                  </span>
                  {option.description ? (
                    <span className={styles.description}>{option.description}</span>
                  ) : null}
                </button>
              )
            : undefined
        }
        onValueChange={(next) => onChange(spec.key, next)}
        disabled={disabled}
      />
    );
    if (!spec.selectsTask) return dropdown;
    return (
      <div className={styles.taskSelection}>
        {dropdown}
        {sourceOptions.find((option) => option.value === value)?.kind === "sandbox" ? (
          <LabelChip
            className={styles.taskKind}
            tone="neutral"
            size="xs"
            variant="filled"
            showIcon={false}
          >
            Sandbox
          </LabelChip>
        ) : null}
      </div>
    );
  }

  // 숫자 값은 NumberInput 과 같은 값에 연결된 선택적 슬라이더를 쓴다.
  // NumberInput 은 범위를 강제하지 않는다. 그래서 변경 값을 spec 의 min 과 max 로 제한한다.
  const num = Number(value);
  // 범위가 모두 정해지면 슬라이더를 표시한다. 범위를 이미지가 주든 앱이 주든 같다.
  const slider = resolveParamSlider(spec);
  // 슬라이더가 아니라 이미지의 범위로 제한한다. 앱이 준 범위는 드래그 범위만 정하고, 사용자가
  // 입력할 수 있는 값은 제한하지 않는다.
  const clamp = (next: number) => {
    let result = spec.type === "int" ? Math.round(next) : next;
    if (spec.min !== undefined) result = Math.max(spec.min, result);
    if (spec.max !== undefined) result = Math.min(spec.max, result);
    return result;
  };
  return (
    <>
      <NumberInput
        aria-label={spec.label}
        value={Number.isFinite(num) ? num : null}
        mode={spec.type === "int" ? "integer" : "decimal"}
        onChange={(next) => onChange(spec.key, clamp(next ?? num))}
      />
      {slider === null ? null : (
        // c6 컨트롤은 가장 가까운 Field 에서 id 를 받는다. 그래서 숫자 입력 옆에 슬라이더만 두면
        // 같은 id 가 두 번 렌더링된다. 슬라이더에 별도 Field 를 주어 다른 id 를 쓰게 한다.
        <Field disabled={disabled}>
          <Slider
            aria-label={`${spec.label} 슬라이더`}
            value={Number.isFinite(num) ? num : slider.min}
            min={slider.min}
            max={slider.max}
            step={slider.step}
            disabled={disabled}
            onValueChange={(next) => onChange(spec.key, clamp(next))}
          />
        </Field>
      )}
    </>
  );
}

// 식별 열에 표시한 이미지 기본값으로 되돌린다. 폼 단위 Restore Defaults 는 다르다. 그 버튼은
// 폼을 열었을 때의 상태로 되돌리고, 제안된 TASK 도 포함한다.
function RevertButton({
  spec,
  value,
  onChange,
  disabled,
}: {
  spec: EvalParamSpec;
  value: EvalParamValue;
  onChange: (key: string, value: EvalParamValue) => void;
  disabled: boolean;
}): ReactNode {
  if (paramValuesEqual(value, spec.default)) {
    return null;
  }
  return (
    <IconButton
      type="button"
      variant="ghost"
      // 이 열의 컨트롤은 c6 `md` 다. 그래서 글리프도 한 단계 아래가 아니라 같은 아이콘 크기를 쓴다.
      size="md"
      aria-label={`${spec.label} 되돌리기`}
      disabled={disabled}
      onClick={() => onChange(spec.key, spec.default)}
    >
      <RestoreIcon />
    </IconButton>
  );
}

function seedCountNote(value: EvalParamValue): ReactNode {
  const count = safeSeedCount(String(value ?? ""));
  if (count === null) {
    return null;
  }
  return <span className={styles.note}>{`Evaluation ${count}개가 실행됩니다.`}</span>;
}
